/**
 * API client (ADR-0015 §3–5). The frontend talks to the backend `/api` ONLY.
 *
 * - Base URL from `VITE_API_BASE_URL` (empty → same-origin/dev-proxy `/api`).
 * - Bearer auth: `Authorization: Bearer <token>` (tenancy is server-derived from
 *   the verified token — the client never supplies org scope).
 * - `VITE_USE_FIXTURES` (default "true") serves local fixtures so the SPA runs
 *   with no backend; set it to "false" to hit the real API.
 */
import { useEffect, useRef, useState } from 'react';
import type { Session } from './session';
import { FIXTURES } from './fixtures';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

export async function apiFetch(session: Session, path: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(init.headers as Record<string, string>) };
  if (session.token) headers['Authorization'] = `Bearer ${session.token}`;
  return fetch(`${BASE}${path}`, { ...init, headers });
}

export interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Force an immediate re-fetch (e.g. after an upload creates a new version). */
  reload: () => void;
}

/**
 * Live polling options. When `pollWhile(data)` is true after a successful fetch,
 * the hook schedules another fetch in `pollMs` (default 4s) — so a version stuck
 * "analysing…" flips to its findings within seconds of the worker finishing,
 * with no manual reload. Polling never runs in fixtures mode.
 */
export interface PollOpts<T> {
  pollMs?: number;
  pollWhile?: (data: T) => boolean;
}

/** Read a resource. Uses fixtures unless VITE_USE_FIXTURES=false. */
export function useResource<T>(session: Session, path: string | null, opts?: PollOpts<T>): Resource<T> {
  const [state, setState] = useState<Resource<T>>({ data: null, loading: path !== null, error: null, reload: () => {} });
  const [nonce, setNonce] = useState(0);
  const reload = (): void => setNonce((n) => n + 1);
  const pollWhileRef = useRef(opts?.pollWhile);
  pollWhileRef.current = opts?.pollWhile;
  const pollMs = opts?.pollMs ?? 4000;
  useEffect(() => {
    if (path === null) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (USE_FIXTURES) {
      const data = fixtureFor<T>(path);
      // simulate async so loading states are exercised
      const t = setTimeout(() => live && setState({ data, loading: false, error: data === null ? 'no fixture for ' + path : null, reload }), 40);
      return () => { live = false; clearTimeout(t); };
    }
    setState((s) => ({ data: s.data, loading: s.data === null, error: null, reload }));
    apiFetch(session, path)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return (await r.json()) as T;
      })
      .then((data) => {
        if (!live) return;
        setState({ data, loading: false, error: null, reload });
        if (pollWhileRef.current?.(data)) timer = setTimeout(() => { if (live) setNonce((n) => n + 1); }, pollMs);
      })
      .catch((e) => live && setState({ data: null, loading: false, error: String(e), reload }));
    return () => { live = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload/pollWhile are stable via ref
  }, [path, session, nonce]);
  return state;
}

/**
 * Read a resource whose REAL API shape differs from the screen shape (P2 drift).
 * Fixtures are already screen-shaped → returned as-is; a live response is the
 * backend shape `TIn` and goes through `map` (see lib/adapt.ts).
 */
export function useResourceMapped<TIn, TOut>(
  session: Session,
  path: string | null,
  map: (data: TIn) => TOut,
  opts?: PollOpts<TOut>,
): Resource<TOut> {
  const [state, setState] = useState<Resource<TOut>>({ data: null, loading: path !== null, error: null, reload: () => {} });
  const [nonce, setNonce] = useState(0);
  const reload = (): void => setNonce((n) => n + 1);
  const pollWhileRef = useRef(opts?.pollWhile);
  pollWhileRef.current = opts?.pollWhile;
  const pollMs = opts?.pollMs ?? 4000;
  useEffect(() => {
    if (path === null) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (USE_FIXTURES) {
      const data = fixtureFor<TOut>(path);
      const t = setTimeout(() => live && setState({ data, loading: false, error: data === null ? 'no fixture for ' + path : null, reload }), 40);
      return () => { live = false; clearTimeout(t); };
    }
    setState((s) => ({ data: s.data, loading: s.data === null, error: null, reload }));
    apiFetch(session, path)
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return map((await r.json()) as TIn);
      })
      .then((data) => {
        if (!live) return;
        setState({ data, loading: false, error: null, reload });
        if (pollWhileRef.current?.(data)) timer = setTimeout(() => { if (live) setNonce((n) => n + 1); }, pollMs);
      })
      .catch((e) => live && setState({ data: null, loading: false, error: String(e), reload }));
    return () => { live = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- map/reload/pollWhile are stable
  }, [path, session, nonce]);
  return state;
}

/** Fixture router — maps an API path onto local data. */
function fixtureFor<T>(path: string): T | null {
  const p = path.split('?')[0];
  const archived = path.includes('archived=1');
  if (p === '/api/portfolio') return FIXTURES.portfolio as unknown as T;
  if (p === '/api/tasks') return FIXTURES.tasks as unknown as T;
  if (p === '/api/art14') return (archived ? FIXTURES.art14Archived : FIXTURES.art14) as unknown as T;
  if (p === '/api/kb') {
    const sp = new URLSearchParams(path.split('?')[1] ?? '');
    const q = (sp.get('q') ?? '').trim().toLowerCase();
    const all = FIXTURES.kb;
    const rows = q
      ? all.filter((v) => v.cveId.toLowerCase().includes(q) || v.affected.some((a) => a.product.toLowerCase().includes(q) || a.versions.toLowerCase().includes(q)))
      : all;
    return { rows, total: rows.length, page: 1, pageSize: 50, mode: q ? 'search' : 'catalog' } as unknown as T;
  }
  if (p === '/api/warnings') return FIXTURES.warnings as unknown as T;
  if (p === '/api/org-profile') return FIXTURES.orgProfile as unknown as T;
  if (p === '/api/members') return FIXTURES.members as unknown as T;
  if (p.startsWith('/api/art14/')) {
    const id = p.split('/')[3];
    return (FIXTURES.art14.find((a) => a.id === id) ?? null) as unknown as T;
  }
  if (p.startsWith('/api/products/')) {
    const id = p.split('/')[3];
    return (FIXTURES.portfolio.products.find((x) => x.id === id) ?? null) as unknown as T;
  }
  return null;
}
